import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import { AI_TOKEN_CALCULATOR_ENDPOINT } from "../../config/urls";
import { formatAICost } from "../../utils/currency";
import { aiFetch } from "./aiApi";

const DELAY_MS = 400;

/**
 * "≈ $X per question" beside a model picker, from the AI Token Calculator for one
 * question at the bot's current settings, so the cost of a switch is visible before
 * saving. kind="chat" prices the chat model; kind="embed" the knowledge-base lookups
 * and training for the embedding model.
 */
export default function AICostHint({ kind = "chat", chatModel, embeddingModel }) {
  const { getValidToken } = useAuth();
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (!chatModel) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const res = await aiFetch(getValidToken, AI_TOKEN_CALCULATOR_ENDPOINT, {
        method: "POST",
        body: { model: chatModel, embedding_model: embeddingModel, users: 1, questions_per_user: 1 },
      });
      if (!cancelled) setEstimate(res.ok ? res.data : null);
    }, DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [chatModel, embeddingModel, getValidToken]);

  if (!estimate) return null;

  const basis =
    estimate.basis_used === "measured" ? "from your last 30 days of questions" : "at your bot's current settings";
  let text;
  if (kind === "embed") {
    const lookup = estimate.breakdown.find((line) => line.call === "query_embedding");
    text = `≈ ${formatAICost((lookup?.cost_per_question_usd || 0) * 1000)} per 1,000 question lookups · ≈ ${formatAICost(
      estimate.training_cost_usd
    )} to train your current knowledge base`;
  } else {
    text = `≈ ${formatAICost(estimate.cost_per_question_usd)} per question ${basis}`;
  }
  return (
    <Typography sx={{ fontSize: "0.8rem", color: "#6C7C93", mt: 0.75 }} data-testid={`ai-cost-hint-${kind}`}>
      {text}
    </Typography>
  );
}
